const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

const parseDate = (value) => {
  if (!value) {
    return null;
  }
  const parts = String(value).split('-');
  if (parts.length !== 3) {
    return null;
  }
  const [year, month, day] = parts.map((part) => Number(part));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateLabel = (value) => {
  const date = parseDate(value);
  if (!date) {
    return '-';
  }
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}年${mm}月${dd}日`;
};

const addDays = (date, days = 1) => {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDayLabel = (value) => {
  const date = parseDate(value);
  if (!date) {
    return '';
  }
  return String(date.getDate()).padStart(2, '0');
};

const buildDateRange = (start, end) => {
  const startDate = parseDate(start);
  const endDate = parseDate(end);
  if (!startDate || !endDate || startDate > endDate) {
    return [];
  }
  const range = [];
  let cursor = new Date(startDate.getTime());
  while (cursor <= endDate) {
    const yyyy = cursor.getFullYear();
    const mm = String(cursor.getMonth() + 1).padStart(2, '0');
    const dd = String(cursor.getDate()).padStart(2, '0');
    range.push(`${yyyy}-${mm}-${dd}`);
    cursor = addDays(cursor, 1);
  }
  return range;
};

const formatMinutes = (minutes) => {
  const value = Number(minutes) || 0;
  const hours = Math.floor(value / 60);
  const remain = value % 60;
  if (hours && remain) {
    return `${hours}時間${remain}分`;
  }
  if (hours) {
    return `${hours}時間`;
  }
  return `${remain}分`;
};

const formatPercent = (value, fractionDigits = 1) => {
  const numeric = Number.isFinite(Number(value)) ? Number(value) : 0;
  return `${numeric.toFixed(fractionDigits)}%`;
};

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

class UserLearningPlanApp {
  constructor() {
    this.root = document.getElementById('user-learning-plan-app');
    if (!this.root) {
      return;
    }

    this.optionsUrl = this.root.dataset.optionsUrl;
    this.planUrl = this.root.dataset.planUrl;
    this.initialCourseId = this.root.dataset.initialCourseId || '';

    this.state = {
      loading: true,
      error: null,
      courses: [],
      selectedCourseId: '',
      selectedPlanId: null,
      planLoading: false,
      currentPlan: null,
      history: [],
    };

    this.fetchOptions();
  }

  setState(patch, callback) {
    this.state = { ...this.state, ...patch };
    this.render();
    if (typeof callback === 'function') {
      callback();
    }
  }

  async fetchOptions() {
    if (!this.optionsUrl) {
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const response = await window.axios.get(this.optionsUrl);
      const courses = response.data?.courses || [];
      const initialCourse = this.initialCourseId
        ? courses.find((course) => String(course.course_id) === String(this.initialCourseId))
        : courses[0];
      const selectedCourseId = initialCourse ? String(initialCourse.course_id) : '';

      this.setState({
        loading: false,
        courses,
        selectedCourseId,
      }, () => {
        if (selectedCourseId) {
          this.fetchPlan(selectedCourseId);
        }
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: this.extractErrorMessage(error),
      });
    }
  }

  async fetchPlan(courseId, planId = null) {
    if (!this.planUrl || !courseId) {
      return;
    }

    this.setState({ planLoading: true, error: null });

    try {
      const response = await window.axios.get(this.planUrl, {
        params: {
          course_id: courseId,
          plan_id: planId,
        },
      });
      const currentPlan = response.data?.active_plan || null;
      const history = response.data?.history || [];

      this.setState({
        planLoading: false,
        currentPlan,
        history,
        selectedPlanId: currentPlan ? currentPlan.id : null,
      });
    } catch (error) {
      this.setState({
        planLoading: false,
        error: this.extractErrorMessage(error),
        currentPlan: null,
        history: [],
      });
    }
  }

  extractErrorMessage(error) {
    if (!error) {
      return '予期せぬエラーが発生しました。';
    }
    if (error.response && error.response.data) {
      if (typeof error.response.data === 'string') {
        return error.response.data;
      }
      if (error.response.data.message) {
        return error.response.data.message;
      }
    }
    if (error.message) {
      return error.message;
    }
    return '予期せぬエラーが発生しました。';
  }

  handleCourseChange(value) {
    this.setState({ selectedCourseId: value, selectedPlanId: null }, () => {
      this.fetchPlan(value);
    });
  }

  handlePlanChange(value) {
    const planId = value ? Number(value) : null;
    this.setState({ selectedPlanId: planId }, () => {
      this.fetchPlan(this.state.selectedCourseId, planId);
    });
  }

  renderSummary(plan) {
    if (!plan) {
      return '';
    }

    const loadClass = plan.weekly_minutes >= 1200
      ? 'user-plan-badge user-plan-badge--danger'
      : plan.weekly_minutes >= 900
        ? 'user-plan-badge user-plan-badge--warning'
        : 'user-plan-badge user-plan-badge--safe';
    const loadLabel = plan.weekly_minutes >= 1200
      ? '危険水位'
      : plan.weekly_minutes >= 900
        ? '注意'
        : '適正';

    const progress = plan.progress || {};
    const actualPercent = Number(progress.actual_percentage ?? progress.percentage ?? 0);
    const expectedPercent = Number(progress.expected_percentage ?? actualPercent);
    const boundedActualPercent = Math.max(0, Math.min(100, actualPercent));
    const boundedExpectedPercent = Math.max(0, Math.min(100, expectedPercent));
    const percentGap = Number(progress.percentage_gap ?? (actualPercent - expectedPercent));

    const percentGapLabel = percentGap === 0
      ? '予定通り'
      : `${percentGap > 0 ? '+' : '-'}${Math.abs(percentGap).toFixed(1)}pt（${percentGap > 0 ? '先行' : '不足'}）`;
    const percentGapClass = percentGap === 0
      ? 'user-plan-diff user-plan-diff--even'
      : percentGap > 0
        ? 'user-plan-diff user-plan-diff--ahead'
        : 'user-plan-diff user-plan-diff--behind';

    const diffMinutes = Number(progress.minutes_gap_to_date ?? 0);
    const diffLabel = diffMinutes === 0
      ? '予定通り'
      : diffMinutes > 0
        ? `+${formatMinutes(diffMinutes)}（先行）`
        : `-${formatMinutes(Math.abs(diffMinutes))}（不足）`;
    const diffClass = diffMinutes === 0
      ? 'user-plan-diff user-plan-diff--even'
      : diffMinutes > 0
        ? 'user-plan-diff user-plan-diff--ahead'
        : 'user-plan-diff user-plan-diff--behind';

    const remainingMinutes = Number(progress.remaining_minutes ?? 0);
    const dailyTargetRemaining = Number(progress.daily_target_remaining ?? 0);
    const elapsedDays = Number(progress.elapsed_days ?? 0);
    const totalDays = Number(progress.total_days ?? 0);
    const remainingDays = Number(progress.remaining_days ?? 0);
    const totalDaysDisplay = totalDays || Math.max(0, (plan.week_count ?? 0) * 7);

    const actualWeekMinutes = Number(progress.actual_week_minutes ?? 0);
    const weeklyGap = Number(progress.weekly_gap_minutes ?? 0);
    const weeklyGapLabel = weeklyGap === 0
      ? '予定通り'
      : `${weeklyGap > 0 ? '+' : '-'}${formatMinutes(Math.abs(weeklyGap))}（${weeklyGap > 0 ? '先行' : '不足'}）`;
    const weeklyGapClass = weeklyGap === 0
      ? 'user-plan-diff user-plan-diff--even'
      : weeklyGap > 0
        ? 'user-plan-diff user-plan-diff--ahead'
        : 'user-plan-diff user-plan-diff--behind';

    const previousWeekMinutes = Number(progress.previous_week_minutes ?? 0);
    const weeklyTrend = progress.weekly_trend || 'flat';
    const weeklyTrendIcon = weeklyTrend === 'up' ? '▲' : weeklyTrend === 'down' ? '▼' : '▬';
    const weeklyTrendText = weeklyTrend === 'up'
      ? '先週より増加'
      : weeklyTrend === 'down'
        ? '先週より減少'
        : '先週と同じペース';

    return `
      <div class="user-plan-summary">
        <div class="user-plan-card">
          <div class="user-plan-card-label">対象コース</div>
          <div class="user-plan-card-value">
            <span class="user-plan-card-number">${escapeHtml(plan.course_name || '')}</span>
          </div>
          <div class="user-plan-card-note">第${escapeHtml(String(plan.version))}版 / ステータス: ${plan.status === 'active' ? '最新' : '過去'}</div>
        </div>
        <div class="user-plan-card">
          <div class="user-plan-card-label">学習期間</div>
          <div class="user-plan-card-value user-plan-card-value--period">
            <span class="user-plan-card-number">${escapeHtml(formatDateLabel(plan.planned_start_date))}</span>
            <span class="user-plan-card-number">${escapeHtml(formatDateLabel(plan.planned_end_date))}</span>
          </div>
          <div class="user-plan-card-note">
            全${escapeHtml(String(totalDaysDisplay))}日（${escapeHtml(String(plan.week_count ?? 0))}週間）<br>
            経過${escapeHtml(String(elapsedDays))}日 / 残り${escapeHtml(String(Math.max(remainingDays, 0)))}日 / テキスト ${escapeHtml(String(plan.text_count ?? 0))} 件
          </div>
        </div>
        <div class="user-plan-card user-plan-card--highlight">
          <div class="user-plan-card-label">週の必要学習時間</div>
          <div class="user-plan-card-value">
            <span class="user-plan-card-number">${escapeHtml(formatMinutes(plan.weekly_minutes))}</span>
          </div>
          <div class="user-plan-progress">
            <span class="user-plan-label-badge">負荷レベル <span class="${loadClass}">${loadLabel}</span></span>
            <span>日の必要学習時間 ${escapeHtml(formatMinutes(plan.daily_minutes))}</span>
          </div>
          <div class="user-plan-weekly-comparison">
            <div class="user-plan-weekly-row">
              <span class="user-plan-weekly-label">今週の実績</span>
              <span class="user-plan-weekly-value">${escapeHtml(formatMinutes(actualWeekMinutes))}</span>
              <span class="${weeklyGapClass}">${escapeHtml(weeklyGapLabel)}</span>
            </div>
            <div class="user-plan-weekly-trend user-plan-weekly-trend--${escapeHtml(weeklyTrend)}">
              <span class="user-plan-weekly-trend__icon">${weeklyTrendIcon}</span>
              <span class="user-plan-weekly-trend__label">${escapeHtml(weeklyTrendText)}（先週 ${escapeHtml(formatMinutes(previousWeekMinutes))}）</span>
            </div>
          </div>
        </div>
        <div class="user-plan-card">
          <div class="user-plan-card-label">進捗状況</div>
          <div class="user-plan-progress-meter">
            <div class="user-plan-progress-meter__bar">
              <div class="user-plan-progress-meter__fill" style="width:${boundedActualPercent}%"></div>
              <div class="user-plan-progress-meter__marker" style="left:${boundedExpectedPercent}%"></div>
            </div>
            <div class="user-plan-progress-meter__label">実績 ${escapeHtml(formatPercent(actualPercent))} / 予定 ${escapeHtml(formatPercent(expectedPercent))}</div>
          </div>
          <div class="user-plan-progress-gap">
            <span>差分</span>
            <span class="${percentGapClass}">${escapeHtml(percentGapLabel)}</span>
          </div>
          <div class="user-plan-progress-detail">
            <span>完了 ${escapeHtml(String(progress.completed || 0))} 件</span>
            <span>学習中 ${escapeHtml(String(progress.in_progress || 0))} 件</span>
            <span>未着手 ${escapeHtml(String(progress.pending || 0))} 件</span>
          </div>
        </div>
        <div class="user-plan-card">
          <div class="user-plan-card-label">今日までの学習実績</div>
          <div class="user-plan-card-value">
            <span class="user-plan-card-number">${escapeHtml(formatMinutes(progress.actual_minutes_to_date || 0))}</span>
          </div>
          <div class="user-plan-progress-gap">
            <span>予定 ${escapeHtml(formatMinutes(progress.expected_minutes_to_date || 0))}</span>
            <span class="${diffClass}">${escapeHtml(diffLabel)}</span>
          </div>
          <div class="user-plan-card-note">
            残り学習時間 ${escapeHtml(formatMinutes(remainingMinutes))} / 1日あたり必要 ${escapeHtml(formatMinutes(dailyTargetRemaining))}
          </div>
        </div>
      </div>
    `;
  }

  renderTimeline(plan) {
    const timelineItems = plan?.timeline || [];
    if (!timelineItems.length) {
      return '<div class="user-plan-empty">表示できる学習バーがありません。</div>';
    }

    const dateRange = buildDateRange(plan.planned_start_date, plan.planned_end_date);
    if (!dateRange.length) {
      return '<div class="user-plan-empty">表示できる期間がありません。</div>';
    }

    const dayCount = dateRange.length;
    const minWidth = Math.max(dayCount * 36, 640);
    const totalWidth = 220 + minWidth;

    const today = new Date();
    const todayKey = formatDateKey(today);
    let todayIndex = dateRange.indexOf(todayKey);
    if (todayIndex === -1) {
      const first = parseDate(dateRange[0]);
      const last = parseDate(dateRange[dateRange.length - 1]);
      if (first && today < first) {
        todayIndex = 0;
      } else if (last && today > last) {
        todayIndex = dateRange.length - 1;
      } else {
        todayIndex = null;
      }
    }
    const todayPercent = todayIndex !== null && todayIndex >= 0
      ? ((todayIndex + 0.5) / dayCount) * 100
      : null;

    const scale = dateRange
      .map((date) => `<span>${escapeHtml(formatDayLabel(date))}</span>`)
      .join('');

    const firstDateObj = parseDate(dateRange[0]);
    const lastDateObj = parseDate(dateRange[dateRange.length - 1]);
    const clampIndex = (value, fallback) => {
      if (!value || !firstDateObj || !lastDateObj) {
        return fallback;
      }
      if (value < firstDateObj) {
        return 0;
      }
      if (value > lastDateObj) {
        return dayCount - 1;
      }
      const key = formatDateKey(value);
      const existing = dateRange.indexOf(key);
      if (existing !== -1) {
        return existing;
      }
      const diffMs = value.getTime() - firstDateObj.getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      return Math.max(0, Math.min(dayCount - 1, diffDays));
    };

    const bars = timelineItems.map((item) => {
      const startDate = parseDate(item.planned_start_date);
      const endDate = parseDate(item.planned_end_date);
      const startIndex = clampIndex(startDate, 0);
      const endIndex = clampIndex(endDate, dayCount - 1);
      const leftIndex = Math.max(0, Math.min(dayCount - 1, Math.min(startIndex, endIndex)));
      const rightIndex = Math.max(0, Math.min(dayCount - 1, Math.max(startIndex, endIndex)));
      const left = (leftIndex / dayCount) * 100;
      const width = ((rightIndex - leftIndex + 1) / dayCount) * 100;
      const isOverdue = todayIndex !== null && todayIndex > endIndex && item.status !== 'completed';
      const statusClass = isOverdue
        ? 'user-plan-timeline__bar--overdue'
        : `user-plan-timeline__bar--${escapeHtml(item.status || 'pending')}`;

      const todayLine = todayPercent !== null
        ? `<div class="user-plan-timeline__today-line" style="left:${todayPercent}%"></div>`
        : '';

      return `
        <div class="user-plan-timeline__row" style="min-width:${totalWidth}px">
          <div class="user-plan-timeline__cell">
            <div class="user-plan-timeline__text">
              ${item.status === 'completed' ? '<span class="user-plan-status-icon">✔</span>' : ''}
              <span>${escapeHtml(item.text_name || '')}</span>
            </div>
          </div>
          <div class="user-plan-timeline__track">
            <div class="user-plan-timeline__track-inner" style="width:${minWidth}px; min-width:${minWidth}px">
              <div class="user-plan-timeline__bar ${statusClass}" style="left:${left}%;width:${width}%"></div>
              ${todayLine}
            </div>
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="user-plan-timeline">
        <div class="user-plan-timeline__header" style="min-width:${totalWidth}px">
          <div class="user-plan-timeline__cell">テキスト</div>
          <div class="user-plan-timeline__track">
            <div class="user-plan-timeline__track-inner user-plan-timeline__track-inner--header" style="width:${minWidth}px; min-width:${minWidth}px">
              <div class="user-plan-timeline__scale">${scale}</div>
              ${todayPercent !== null ? `<div class="user-plan-timeline__today-line" style="left:${todayPercent}%"></div>` : ''}
            </div>
          </div>
        </div>
        <div class="user-plan-timeline__body" style="min-width:${totalWidth}px">
          ${bars}
        </div>
      </div>
    `;
  }

  render() {
    if (this.state.loading) {
      this.root.innerHTML = '<div class="user-plan-loading">読み込み中...</div>';
      return;
    }

    if (this.state.error && !this.state.planLoading && !this.state.currentPlan) {
      this.root.innerHTML = `<div class="user-plan-error">${escapeHtml(this.state.error)}</div>`;
      return;
    }

    const courseOptions = this.state.courses.map((course) => (
      `<option value="${course.course_id}" ${String(course.course_id) === String(this.state.selectedCourseId) ? 'selected' : ''}>${escapeHtml(course.course_name || '')}</option>`
    )).join('');

    const uniquePlans = [];
    const seen = new Set();
    const appendPlan = (plan) => {
      if (plan && !seen.has(plan.id)) {
        seen.add(plan.id);
        uniquePlans.push(plan);
      }
    };
    appendPlan(this.state.currentPlan);
    (this.state.history || []).forEach((plan) => appendPlan(plan));
    uniquePlans.sort((a, b) => (b.version || 0) - (a.version || 0));

    const showVersionSelect = uniquePlans.length > 1;
    const planSelectOptions = uniquePlans.map((plan) => {
      const selected = this.state.currentPlan && String(plan.id) === String(this.state.currentPlan.id) ? 'selected' : '';
      const suffix = plan.status === 'active' ? '（最新）' : '';
      return `<option value="${escapeHtml(String(plan.id))}" ${selected}>第${escapeHtml(String(plan.version))}版${suffix}</option>`;
    }).join('');

    const summarySection = this.renderSummary(this.state.currentPlan);
    const timelineSection = this.renderTimeline(this.state.currentPlan);

    const alertSection = this.state.error && !this.state.planLoading
      ? `<div class="user-plan-error">${escapeHtml(this.state.error)}</div>`
      : '';

    let content;
    if (this.state.planLoading) {
      content = '<div class="user-plan-loading">スケジュールを読み込み中...</div>';
    } else if (!this.state.currentPlan) {
      content = '<div class="user-plan-empty">このコースには学習計画がまだ登録されていません。</div>';
    } else {
      content = `
        ${summarySection}
        ${timelineSection}
      `;
    }

    this.root.innerHTML = `
      ${alertSection}
      <div class="user-plan-controls">
        <div class="user-plan-control">
          <label for="user-plan-course-select">コース</label>
          <select id="user-plan-course-select" data-role="course-select">
            ${courseOptions}
          </select>
        </div>
        ${showVersionSelect ? `
          <div class="user-plan-control">
            <label for="user-plan-version-select">表示する計画</label>
            <select id="user-plan-version-select" data-role="plan-select">
              ${planSelectOptions}
            </select>
          </div>
        ` : ''}
      </div>
      ${content}
    `;

    this.attachEvents();
  }

  attachEvents() {
    const courseSelect = this.root.querySelector('[data-role="course-select"]');
    if (courseSelect) {
      courseSelect.addEventListener('change', (event) => this.handleCourseChange(event.target.value));
    }

    const planSelect = this.root.querySelector('[data-role="plan-select"]');
    if (planSelect) {
      planSelect.addEventListener('change', (event) => this.handlePlanChange(event.target.value));
    }

  }

}

export default UserLearningPlanApp;
